//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useState } from 'react'
import { Box, Modal, Typography, TextField, Button, IconButton, Paper } from '@mui/material'
import { Close as CloseIcon, Remove as RemoveIcon } from '@mui/icons-material'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { companyProfilePut } from '@react/fetchers/companyProfileFetchers'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { chainActions } from '@react/redux/chainSlice'
import { logOT } from '@shared/loggingShared'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { onPromise } from '@react/components/UiHelpers'

interface EditEmailsModalProps {
  open: boolean
  onClose: () => void
  companyProfile: CompanyProfileCTO
  onSuccess?: (updatedProfile: CompanyProfileCTO) => void
}

// Schema for email editing
const EditEmailsSchema = yup.object({
  emails: yup
    .array()
    .of(yup.string().email('Please enter a valid email address').required('Email is required'))
    .min(1, 'At least one email is required'),
})

type EditEmailsFormData = yup.InferType<typeof EditEmailsSchema>

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0px 16px 50px rgba(48, 49, 51, 0.08), 0px 0px 0.5px #303133',
  p: 4,
}

export default function EditEmailsModal({
  open,
  onClose,
  companyProfile,
  onSuccess,
}: EditEmailsModalProps): ReactElement {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    cognitoUser,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()
  const dispatch = useOtDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<EditEmailsFormData>({
    resolver: yupResolver(EditEmailsSchema),
    defaultValues: {
      emails: companyProfile.emails,
    },
  })

  // Watch emails to handle dynamic email fields
  const watchedEmails = watch('emails')

  // Reset form when companyProfile changes
  React.useEffect(() => {
    reset({
      emails: companyProfile.emails,
    })
  }, [companyProfile, reset])

  const onErrors = (errors: any) => {
    logOT.info('ERRORS: ', errors)
  }

  const onSubmit: SubmitHandler<EditEmailsFormData> = async (data) => {
    if (!serverApiBaseUrl && !(cognitoUser || location.hostname === 'localhost')) {
      logOT.error('Missing required configuration for saving company profile')
      return
    }

    setIsSubmitting(true)
    try {
      const updatedProfile: CompanyProfileCTO = {
        ...companyProfile,
        emails: data.emails?.filter((email) => email.trim() !== '') || [],
      }

      const savedProfile = await companyProfilePut(serverApiBaseUrl, updatedProfile, restAPIKey, cognitoUser!)
      dispatch(chainActions.updateCompanyProfile(savedProfile))

      onSuccess?.(savedProfile)
      onClose()
    } catch (error) {
      logOT.error('Error saving company profile emails:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddEmail = () => {
    const currentEmails = watch('emails') || []
    setValue('emails', [...currentEmails, ''])
  }

  const handleRemoveEmail = (index: number) => {
    const currentEmails = watch('emails') || []
    const newEmails = currentEmails.filter((_, i) => i !== index)
    setValue('emails', newEmails)
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper sx={modalStyle}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Email Addresses
          </Typography>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <form onSubmit={onPromise(handleSubmit(onSubmit, onErrors))}>
          {/* Email Addresses */}
          <Box display="flex" flexDirection="column" gap={2} mb={3}>
            {(watchedEmails || []).map((_email, index) => (
              <Box key={index} display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  {...register(`emails.${index}` as const)}
                  variant="outlined"
                  size="small"
                  placeholder="Enter email address"
                  error={!!errors.emails?.[index]}
                  helperText={errors.emails?.[index]?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEmail(index)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    '&:hover': {
                      backgroundColor: '#d0d0d0',
                    },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="text"
              onClick={handleAddEmail}
              sx={{
                alignSelf: 'flex-start',
                mt: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                color: '#000',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              ADD EMAIL
            </Button>
          </Box>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Modal>
  )
}
